<template>
  <RygenQuickSearch
    :results="resolvedResults"
    option-group-label="header"
    option-group-children="items"
    option-label="label"
    @clear="onClear"
    @search="onSearch"
    @select="onSelect"
  >
    <template #result="{ option }: { option: Result }">
      <QuickSearchResultView
        v-if="option.searchType === 'QuickSearch'"
        :result="option"
      />
      <RecentlyViewedResultView
        v-else
        :recent="option"
        @domain-branding-refresh="onDomainBrandingRefresh"
      />
    </template>

    <template #loading>
      <QuickSearchLoadingCard />
    </template>
  </RygenQuickSearch>
</template>

<script lang="ts" setup>
import { CancelTokenSource, default as axios } from 'axios';
import debounce from 'lodash/debounce';
import type { Ref } from 'vue';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import authorization from '@rygen/rygen-ui/authorization';
import { Loadable } from '@rygen/rygen-ui/model';

import api from '@/api';
import { useRecentlyViewed } from '@/composables';
import {
  QuickSearchResult,
  QuickSearchResultWrapper,
  RecentlyViewedResult,
} from '@/model';

import QuickSearchLoadingCard from '@/components/search/QuickSearchLoadingCard.vue';
import QuickSearchResultView from '@/components/search/QuickSearchResult.vue';
import RecentlyViewedResultView from '@/components/search/RecentlyViewedResult.vue';
import RygenQuickSearch from '@rygen/rygen-ui/components/ui/QuickSearch.vue';

type Result = QuickSearchResult | RecentlyViewedResult;
type Results = QuickSearchResult[] | RecentlyViewedResult[];
type OptionGroup = { header: string; items: Results };

const { t } = useI18n();
const { onDomainBrandingRefresh, recentlyViewed } = useRecentlyViewed();

const cancelRequest: Ref<CancelTokenSource | undefined> = ref(undefined);
const results: Ref<
  Loadable<QuickSearchResultWrapper | RecentlyViewedResult[]>
> = ref({ loading: false });

const resolvedResults = computed((): Loadable<OptionGroup[]> => {
  const { loading, data, error } = results.value;

  if (loading) {
    return { data: [], loading };
  } else if (error != null) {
    // Don't fall through to stale data from a previous search
    return { data: [], loading, error };
  } else if (data === undefined) {
    return { data: [], loading };
  } else if (data instanceof QuickSearchResultWrapper) {
    const orders = data?.orders;
    const shipments = data?.shipments;

    const networkResults: OptionGroup[] = [];
    if (authorization.hasAuthority('order', 'read') && orders?.length) {
      networkResults.push({ header: t('orders.orders', 2), items: orders });
    }

    if (authorization.hasAuthority('shipment', 'read') && shipments?.length) {
      networkResults.push({
        header: t('shipments.shipments', 2),
        items: shipments,
      });
    }

    return { loading, data: networkResults };
  } else {
    const optionGroup: OptionGroup = {
      header: t('common.recentlyViewed'),
      items: data,
    };
    return {
      loading: false,
      data: [optionGroup],
    };
  }
});

const showRecents = async () => {
  results.value.loading = true;
  // Have to wrap the recents in a promise an await it because PrimeVue's
  // AutoComplete doesn't respond if you just set the data
  results.value.data = await new Promise((resolve) => {
    const recents = recentlyViewed.value.filter((r) => !!r.id);
    resolve(recents);
  });
  results.value.loading = false;
};

const executeSearch = async (searchText: string) => {
  if (cancelRequest.value) {
    cancelRequest.value.cancel();
  }
  cancelRequest.value = axios.CancelToken.source();

  if (!searchText.trim()) {
    return showRecents();
  }

  results.value.loading = true;
  try {
    results.value.data = await api.quickSearch.search(
      searchText,
      cancelRequest.value,
    );
  } catch (e) {
    if (axios.isCancel(e)) {
      return;
    }

    console.error(e);
    results.value.error = e;
  }
  results.value.loading = false;
};

const debounceSearch = debounce(executeSearch, 500);

const onSearch = (searchText: string) => {
  // Clear any error from the previous search as soon as the query changes
  results.value.error = undefined;
  debounceSearch(searchText);
};

const onClear = () => {
  results.value.data = undefined;
  results.value.error = undefined;
};

const onSelect = (_result: QuickSearchResult | RecentlyViewedResult) => {
  const contentHeading = document.querySelector(
    '#content-heading',
  ) as HTMLElement | null;

  // AutoComplete sets focus somewhere; wait til its done to set it ourselves
  window.setTimeout(() => contentHeading?.focus(), 500);
};
</script>
